import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAttachModelVersionFile } from '@/api/model-versions'
import { useModel } from '@/api/models'
import { useWorkspace } from '@/api/workspace'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { useFileUploads } from '@/components/model-versions/file-uploads'
import { FilePicker, UploadList } from '@/components/model-versions/version-files'
import { VersionForm } from '@/components/model-versions/version-form'

// Upload Model version (UI-SCREENS P-06, #101): files, notes, the breaking flag and the Recipe.
// Owner only; the API refuses Staff too. Picked files upload straight away (per-file progress,
// 03f #104) and are attached once the version is saved; saving waits for the uploads. When a file
// fails to attach, the version is saved anyway and the screen links to it (P-07) to add it again.
export const Route = createFileRoute('/w/$slug/products/$productId/models/$modelId/versions/new')({
  component: NewModelVersion,
  staticData: { breadcrumb: 'modelVersions.form.newTitle' },
})

function NewModelVersion() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { slug, productId, modelId } = Route.useParams()
  const workspace = useWorkspace(slug)
  const model = useModel(slug, Number(modelId))
  const uploads = useFileUploads(slug)
  const attach = useAttachModelVersionFile()
  const [attaching, setAttaching] = useState(false)
  const [savedWithErrors, setSavedWithErrors] = useState<number | null>(null)
  const toModel = () =>
    navigate({ to: '/w/$slug/products/$productId/models/$modelId', params: { slug, productId, modelId } })
  const toVersion = (versionNo: number) =>
    navigate({
      to: '/w/$slug/products/$productId/models/$modelId/versions/$versionNo',
      params: { slug, productId, modelId, versionNo: String(versionNo) },
    })

  async function onSaved(created: { id: number; version_no: number }) {
    setAttaching(true)
    const failed = await uploads.attachReady((signedId) =>
      attach.mutateAsync({ params: { path: { slug, model_version_id: created.id } }, body: { file: { signed_id: signedId } } }),
    )
    setAttaching(false)
    if (failed === 0) return toVersion(created.version_no)
    setSavedWithErrors(created.version_no)
  }

  if (workspace.data && workspace.data.role !== 'owner') {
    return <Navigate to="/w/$slug/products/$productId/models/$modelId" params={{ slug, productId, modelId }} replace />
  }
  if (model.isPending) return <LoadingSkeleton variant="page" />
  if (model.isError || !model.data || String(model.data.product_id) !== productId) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="font-heading text-xl font-semibold">{t('modelVersions.form.newTitle')}</h1>
        <p className="text-sm text-muted-foreground">{model.data.name}</p>
      </div>
      <p className="max-w-xl text-sm text-muted-foreground">{t('modelVersions.form.description')}</p>

      <section className="flex max-w-xl flex-col gap-3">
        <h2 className="font-medium">{t('modelVersions.files.title')}</h2>
        <FilePicker
          id="new-version-files"
          disabled={uploads.busy || attaching || savedWithErrors !== null}
          onPick={(files) => uploads.add(files)}
        />
        <UploadList items={uploads.items} />
      </section>

      {savedWithErrors !== null ? (
        <div className="flex max-w-xl flex-col gap-2">
          <p role="alert" className="text-sm text-destructive">
            {t('modelVersions.files.someFailed')}
          </p>
          <Link
            to="/w/$slug/products/$productId/models/$modelId/versions/$versionNo"
            params={{ slug, productId, modelId, versionNo: String(savedWithErrors) }}
            className="text-sm font-medium underline-offset-4 hover:underline"
          >
            {t('modelVersions.files.openVersion')}
          </Link>
        </div>
      ) : (
        <VersionForm
          slug={slug}
          model={model.data}
          busy={uploads.busy || attaching}
          onCancel={toModel}
          onSaved={onSaved}
        />
      )}
    </section>
  )
}
